class SweProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabButtons = Array.from(this.querySelectorAll('[data-swe-tab-target]'));
    this.mobileButtons = Array.from(this.querySelectorAll('[data-swe-mobile-tab-target]'));
    this.panels = Array.from(this.querySelectorAll('[data-swe-tab-panel]'));

    if (!this.panels.length) return;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleHashChange = this.handleHashChange.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');

    this.tabButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.activate(button.dataset.sweTabTarget);
      });
      button.addEventListener('keydown', this.handleTabKeydown);
    });

    this.mobileButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const panelId = button.dataset.sweMobileTabTarget;
        const panel = this.panels.find((candidate) => candidate.id === panelId);
        this.activate(this.mobileMediaQuery.matches && panel?.classList.contains('is-active') ? null : panelId);
      });
    });

    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('hashchange', this.handleHashChange);
    this.mobileMediaQuery.addEventListener('change', this.handleBreakpointChange);

    this.dataset.enhanced = 'true';

    const hashPanel = this.panelFromHash(window.location.hash);
    const initialPanel = hashPanel || this.panels.find((panel) => panel.classList.contains('is-active')) || this.panels[0];
    this.activate(initialPanel.id, { scroll: Boolean(hashPanel) });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('hashchange', this.handleHashChange);
    this.mobileMediaQuery?.removeEventListener('change', this.handleBreakpointChange);
    this.tabButtons?.forEach((button) => button.removeEventListener('keydown', this.handleTabKeydown));
  }

  panelFromHash(hash) {
    if (!hash || hash === '#') return null;

    const targetId = decodeURIComponent(hash.slice(1));
    return this.panels.find((panel) => panel.id === targetId) || null;
  }

  activate(panelId, options = {}) {
    const panel = panelId === null ? null : this.panels.find((candidate) => candidate.id === panelId);
    if (panelId !== null && !panel) return;

    this.panels.forEach((candidate) => {
      const isActive = candidate === panel;
      candidate.classList.toggle('is-active', isActive);
      candidate.removeAttribute('aria-hidden');

      const mobileButton = candidate.querySelector('[data-swe-mobile-tab-target]');
      if (mobileButton) {
        mobileButton.setAttribute('aria-expanded', String(isActive));
        const icon = mobileButton.querySelector('.swe-product-tabs__mobile-icon');
        if (icon) icon.textContent = isActive ? '−' : '+';
      }
    });

    this.tabButtons.forEach((button) => {
      const isActive = button.dataset.sweTabTarget === panel?.id;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;
    });

    if (options.scroll) {
      window.requestAnimationFrame(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  handleDocumentClick(event) {
    const link = event.target.closest('[data-swe-more-details]');
    if (!link) return;

    const panel = this.panelFromHash(link.hash);
    if (!panel) return;

    event.preventDefault();
    this.activate(panel.id, { scroll: true });
  }

  handleHashChange() {
    const panel = this.panelFromHash(window.location.hash);
    if (!panel) return;

    this.activate(panel.id, { scroll: true });
  }

  handleBreakpointChange() {
    if (!this.mobileMediaQuery.matches && !this.panels.some((panel) => panel.classList.contains('is-active'))) {
      this.activate(this.panels[0].id);
    }
  }

  handleTabKeydown(event) {
    const currentIndex = this.tabButtons.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabButtons.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabButtons.length) % this.tabButtons.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabButtons.length - 1;

    if (nextIndex === currentIndex) return;

    event.preventDefault();
    const nextButton = this.tabButtons[nextIndex];
    this.activate(nextButton.dataset.sweTabTarget);
    nextButton.focus();
  }
}

if (!customElements.get('swe-product-tabs')) {
  customElements.define('swe-product-tabs', SweProductTabs);
}
